import {
  FiHome,
  FiGrid,
  FiImage,
  FiVideo,
  FiVolumeX,
  FiUsers,
  FiCreditCard,
  FiSettings,
  FiShield,
  FiBarChart,
  FiPlayCircle,
  FiShoppingBag,
  FiTag,
  FiPackage,
  FiAward,
  FiTarget,
  FiUserPlus,
  FiTrendingUp,
  FiCheckSquare,
  FiZap,
  FiEye,
  FiGift,
  FiFileText,
} from "react-icons/fi";
import {
  AdminModule,
  AdminModulePermissions,
  AdminRole,
  canAccessModule,
  roleSatisfies,
} from "../../auth/roles";

/** IA grouping from the brief §5 / mockup app-shell. Top-level only. */
export type SidebarGroup = "catalog" | "operations";

export interface MenuItem {
  id: string;
  /** English fallback label (used if `labelKey` is absent). */
  label: string;
  /** i18n key under the `main` namespace; preferred over `label` when present. */
  labelKey?: string;
  icon: any;
  path?: string;
  count?: number;
  badge?: string;
  isExpandable?: boolean;
  children?: MenuItem[];
  /** Sidebar section this top-level item belongs to (Dashboard/Settings: none). */
  group?: SidebarGroup;
  /** Minimum admin tier needed to see this item. Defaults to support. */
  minRole?: AdminRole;
  /**
   * Admin module this item belongs to. Children inherit it from their parent
   * unless they set their own. A `manager` only ever sees items whose module it
   * has been granted — an item with no module is invisible to a manager unless
   * it is flagged `managerVisible`.
   */
  module?: AdminModule;
  /** Module-less item a manager may still see (the dashboard home). */
  managerVisible?: boolean;
}

/**
 * Counts the sidebar can actually show. Every field here is fetched.
 *
 * ADM D8 — `usersCount`, `subscriptionsCount` and `ticketsCount` used to live
 * here with no API behind them: they resolved to 0 after load, and while
 * loading the sidebar rendered 1247 / 892 / 23 from a `defaultSidebarCounts`
 * constant. Every page load flashed three invented numbers. A count is real or
 * it is absent — an invented one in an operations panel is worse than none,
 * because it is indistinguishable from a measured one.
 */
export interface SidebarCounts {
  categoriesCount: number;
  imagesCount: number;
  videosCount: number;
  audiosCount: number;
}

/**
 * Creates dynamic sidebar menu items with real-time counts
 * This function replaces hardcoded values with API-driven data
 */
/**
 * `undefined` renders no badge; a number renders one. Zero is deliberately
 * treated as "nothing to show" rather than a badge reading `0` — a category
 * count of zero is not information an operator needs in a nav item.
 */
const badgeCount = (value: number | undefined): number | undefined =>
  value && value > 0 ? value : undefined;

export const createSidebarMenuItems = (counts: SidebarCounts): MenuItem[] => [
  {
    id: "dashboard",
    label: "Dashboard",
    labelKey: "nav.dashboard",
    icon: FiHome,
    path: "/",
    managerVisible: true,
  },
  {
    id: "content",
    label: "Content",
    labelKey: "nav.content",
    icon: FiGrid,
    group: "catalog",
    module: AdminModule.CONTENT,
    isExpandable: true,
    children: [
      {
        id: "categories",
        label: "Categories",
        labelKey: "nav.categories",
        icon: FiGrid,
        path: "/categories",
        count: badgeCount(counts.categoriesCount),
      },
      {
        id: "images",
        label: "Images",
        labelKey: "nav.images",
        icon: FiImage,
        path: "/images",
        count: badgeCount(counts.imagesCount),
      },
      {
        id: "videos",
        label: "Videos",
        labelKey: "nav.videos",
        icon: FiVideo,
        path: "/videos",
        count: badgeCount(counts.videosCount),
      },
      {
        id: "audios",
        label: "Audio Files",
        labelKey: "nav.audios",
        icon: FiVolumeX,
        path: "/audios",
        count: badgeCount(counts.audiosCount),
      },
      {
        id: "onboarding",
        label: "Onboarding",
        labelKey: "nav.onboarding",
        icon: FiPlayCircle,
        path: "/onboarding",
      },
      {
        // SEO D19 — knowledge-base articles written and published here.
        // Inherits AdminModule.CONTENT: the backend maps `/admin/articles`
        // to CONTENT (no new cross-repo module value). Read access shows the
        // list and the read-only editor; writes are gated inside the pages.
        id: "articles",
        label: "Articles",
        labelKey: "nav.articles",
        icon: FiFileText,
        path: "/articles",
      },
    ],
  },
  {
    // AST-P2: topic in, reviewed cards out. Sits in `catalog` because what it
    // produces is catalog content, and the reviewer is the same person.
    id: "asset-pipeline",
    label: "Asset pipeline",
    labelKey: "nav.assetPipeline",
    icon: FiZap,
    group: "catalog",
    module: AdminModule.CONTENT,
    path: "/asset-pipeline",
  },
  {
    id: "marketplace",
    label: "Marketplace",
    labelKey: "nav.marketplace",
    icon: FiShoppingBag,
    group: "catalog",
    module: AdminModule.MARKETPLACE,
    isExpandable: true,
    children: [
      {
        id: "marketplace-listings",
        label: "Listings",
        labelKey: "nav.listings",
        icon: FiTag,
        path: "/marketplace/listings",
      },
      {
        id: "marketplace-packs",
        label: "Packs",
        labelKey: "nav.packs",
        icon: FiPackage,
        path: "/marketplace/packs",
      },
      {
        id: "marketplace-disciplines",
        label: "Disciplines & tags",
        labelKey: "nav.disciplines",
        icon: FiAward,
        path: "/marketplace/disciplines",
      },
    ],
  },
  {
    // Games — Magic Reveal admin levels (SDD 2026-09-18). Reuses
    // AdminModule.CONTENT rather than a new module: the backend already maps
    // `/admin/games/magic-reveal/*` to CONTENT, and AdminModule is a closed
    // enum mirrored from the backend's own — extending it needs a coordinated
    // cross-repo change plus a migration of every manager's grant map.
    id: "games",
    label: "Games",
    labelKey: "nav.games",
    icon: FiZap,
    group: "catalog",
    module: AdminModule.CONTENT,
    isExpandable: true,
    children: [
      {
        id: "games-magic-reveal",
        label: "Magic Reveal",
        labelKey: "nav.magicReveal",
        icon: FiEye,
        path: "/games/magic-reveal",
      },
      {
        id: "games-magic-reveal-backgrounds",
        label: "Backgrounds",
        labelKey: "nav.magicRevealBackgrounds",
        icon: FiImage,
        path: "/games/magic-reveal/backgrounds",
      },
    ],
  },
  {
    id: "users",
    label: "Users",
    labelKey: "nav.users",
    icon: FiUsers,
    group: "operations",
    module: AdminModule.USERS,
    isExpandable: true,
    children: [
      {
        id: "all-users",
        label: "All Users",
        labelKey: "nav.allUsers",
        icon: FiUsers,
        path: "/admin/users",
      },
      {
        // ADM D7 — the "Permissions" entry pointed at the same component as
        // "All Users". One page, one entry.
        id: "admins",
        label: "Administrators",
        labelKey: "nav.admins",
        icon: FiShield,
        path: "/admin/users/admins",
        minRole: AdminRole.SUPERADMIN,
      },
    ],
  },
  {
    /**
     * ADM D1 — Billing is top level, not a tab inside CRM.
     *
     * The split line is *has this person paid us*: CRM is for people who have
     * not yet, Billing for people who have. Merging them cost more than a
     * click — `filterMenuByRole` drops a parent before it looks at its
     * children, so a manager granted SUBSCRIPTIONS but not CRM had no way in
     * at all, and the old top-level path only redirected back into CRM (F5).
     *
     * D2 — each child is a real route rather than a tab, so a Billing view can
     * be linked, bookmarked and survives a refresh. None of the four could
     * before.
     */
    id: "billing",
    label: "Billing",
    labelKey: "nav.billing",
    icon: FiCreditCard,
    group: "operations",
    module: AdminModule.SUBSCRIPTIONS,
    isExpandable: true,
    children: [
      {
        id: "billing-subscribers",
        label: "Subscribers",
        labelKey: "nav.billingSubscribers",
        icon: FiCreditCard,
        path: "/billing/subscribers",
      },
      {
        id: "billing-plans",
        label: "Plans",
        labelKey: "nav.billingPlans",
        icon: FiSettings,
        path: "/billing/plans",
      },
      {
        id: "billing-features",
        label: "Features",
        labelKey: "nav.billingFeatures",
        icon: FiZap,
        path: "/billing/features",
      },
      {
        // ADM D16/P2-3 — admin-only, and the NAV says so. The roster is
        // admin-tier on the server; offering a support principal a link that
        // can only 403 would be F3 with better intentions.
        id: "billing-early-access",
        label: "Early access",
        labelKey: "nav.billingEarlyAccess",
        icon: FiAward,
        path: "/billing/early-access",
        minRole: AdminRole.ADMIN,
      },
      {
        // GRW D21 §1/§6 — support-tier to READ, which is the default here, so
        // no `minRole`: setting one (even SUPPORT) would hide the entry from
        // every manager, since a manager never satisfies a tier. A manager is
        // decided by the SUBSCRIPTIONS module inherited from the billing
        // group (read or write). Revoke, edit, seed and Verify are gated
        // inside the page, and the server decides each of them again.
        id: "billing-offers",
        label: "Offers",
        labelKey: "nav.billingOffers",
        icon: FiGift,
        path: "/billing/offers",
      },
    ],
  },
  {
    id: "crm",
    label: "CRM",
    labelKey: "nav.crm",
    icon: FiTarget,
    group: "operations",
    module: AdminModule.CRM,
    isExpandable: true,
    children: [
      {
        id: "crm-leads",
        label: "Leads",
        labelKey: "nav.crmLeads",
        icon: FiUserPlus,
        path: "/crm",
      },
      {
        id: "crm-pipeline",
        label: "Pipeline",
        labelKey: "nav.crmPipeline",
        icon: FiTrendingUp,
        path: "/crm/pipeline",
      },
      {
        id: "crm-tasks",
        label: "Tasks",
        labelKey: "nav.crmTasks",
        icon: FiCheckSquare,
        path: "/crm/tasks",
      },
      {
        id: "crm-insights",
        label: "Insights",
        labelKey: "nav.crmInsights",
        icon: FiBarChart,
        path: "/crm/insights",
      },
      {
        id: "crm-spend",
        label: "Marketing Spend",
        labelKey: "nav.crmSpend",
        icon: FiTrendingUp,
        path: "/crm/spend",
      },
    ],
  },
  {
    id: "analytics",
    label: "Analytics",
    labelKey: "nav.analytics",
    icon: FiBarChart,
    group: "operations",
    module: AdminModule.ANALYTICS,
    path: "/admin/analytics",
  },
  // ADM D7 — the Support group is gone. "Support Tickets" (with an "urgent"
  // badge and an invented count of 23) and "Documentation" had no routes at
  // all: both fell through the router's `*` catch-all to the dashboard, with
  // no error and no explanation, and ⌘K offered them too. A menu entry whose
  // only behaviour is a redirect is worse than its absence. It returns when a
  // page exists — `nav-integrity.test.ts` is what will keep that honest.
  {
    id: "settings",
    label: "Settings",
    labelKey: "nav.settings",
    icon: FiSettings,
    path: "/admin/settings",
    minRole: AdminRole.SUPERADMIN,
  },
];

/**
 * Filter the menu tree to the items the given principal may see. Drops an
 * expandable parent when all of its children are filtered out.
 *
 * TIER roles (support/admin/superadmin): items with no `minRole` are visible to
 * any admin (support+) — unchanged behaviour.
 *
 * MANAGER: fails closed. An item is visible only when the manager's grant map
 * covers its module (inherited from the parent when the child sets none). An
 * item with no module at all — Support, Settings, the superadmin-only
 * Administrators/Permissions entries — is invisible to a manager unless it is
 * explicitly flagged `managerVisible` (only the Dashboard home is).
 *
 * NOTE: a parent's own `minRole` is evaluated BEFORE recursing into its
 * children — so a parent the role can't meet is removed wholesale (its
 * children are never shown), which is intentional. If one ever should gate the
 * parent independently of its children, revisit this ordering deliberately
 * rather than "fixing" it by accident.
 */
export const filterMenuByRole = (
  items: MenuItem[],
  role: AdminRole | null,
  modulePermissions?: AdminModulePermissions | null,
  inheritedModule?: AdminModule,
): MenuItem[] =>
  items
    .filter((item) =>
      isMenuItemVisible(item, role, modulePermissions, inheritedModule),
    )
    .map((item) =>
      item.children
        ? {
            ...item,
            children: filterMenuByRole(
              item.children,
              role,
              modulePermissions,
              item.module ?? inheritedModule,
            ),
          }
        : item,
    )
    .filter((item) => !item.isExpandable || (item.children?.length ?? 0) > 0);

const isMenuItemVisible = (
  item: MenuItem,
  role: AdminRole | null,
  modulePermissions: AdminModulePermissions | null | undefined,
  inheritedModule: AdminModule | undefined,
): boolean => {
  if (item.minRole && !roleSatisfies(role, item.minRole)) return false;
  if (role !== AdminRole.MANAGER) return true;
  if (item.managerVisible) return true;
  const module = item.module ?? inheritedModule;
  if (!module) return false;
  return canAccessModule(role, modulePermissions, module, "read");
};

/**
 * Default sidebar menu items with fallback values
 * Used during loading states to prevent layout shifts
 */
/**
 * The tree rendered while the counts are still loading (ADM D8).
 *
 * Built from zeroes, which `badgeCount` renders as no badge at all. It used to
 * be built from `defaultSidebarCounts` — invented numbers that flashed on every
 * page load and then dropped to the real values. A nav item briefly showing no
 * badge is a loading state; one showing 1247 is a lie.
 */
export const defaultSidebarMenuItems: MenuItem[] = createSidebarMenuItems({
  categoriesCount: 0,
  imagesCount: 0,
  videosCount: 0,
  audiosCount: 0,
});

/**
 * Legacy export for backward compatibility
 * @deprecated Use createSidebarMenuItems with dynamic counts instead
 */
export { defaultSidebarMenuItems as sidebarMenuItems };

// Helper function to find menu item by path
export const findMenuItemByPath = (path: string): MenuItem | null => {
  const findInItems = (items: MenuItem[]): MenuItem | null => {
    for (const item of items) {
      if (item.path === path) {
        return item;
      }
      if (item.children) {
        const found = findInItems(item.children);
        if (found) return found;
      }
    }
    return null;
  };

  return findInItems(defaultSidebarMenuItems);
};

// Helper function to get breadcrumb for current path
export const getBreadcrumb = (path: string): string[] => {
  const breadcrumb: string[] = [];

  const findBreadcrumb = (
    items: MenuItem[],
    currentPath: string[],
    targetPath: string,
  ): boolean => {
    for (const item of items) {
      const newPath = [...currentPath, item.label];

      if (item.path === targetPath) {
        breadcrumb.push(...newPath);
        return true;
      }

      if (item.children && findBreadcrumb(item.children, newPath, targetPath)) {
        return true;
      }
    }
    return false;
  };

  findBreadcrumb(defaultSidebarMenuItems, [], path);
  return breadcrumb;
};
